import React from "react";
import CodeBlock from "@/components/CodeBlock";

const HelpDoc = () => {
  return (
    <section className="mb-20 text-slate-300">
      {/* TITLE */}
      <h2 className="text-4xl font-bold text-white mb-4">
        AxisNet CLI — Command Reference
      </h2>
      <p className="text-lg mb-10 text-slate-400 leading-relaxed">
        The AxisNet CLI is a high-level scaffolding and automation tool designed
        to accelerate development across Domain, Application, and Infrastructure
        layers. It follows a clean, predictable command structure similar to
        modern frameworks like <strong>Angular CLI</strong> or{" "}
        <strong>NestJS CLI</strong>, but optimized for enterprise-grade .NET
        architectures.
      </p>

      {/* ================================================
          CORE SETUP COMMANDS
      ================================================= */}
      <div className="bg-slate-900/60 border border-slate-800 p-7 rounded-xl mb-14 shadow-lg shadow-black/20">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-2">
          Core Setup Commands
        </h3>
        <p className="text-slate-400 text-sm mb-4">
          Core initialization commands used to establish project context and
          validate AxisNet configuration integrity.
        </p>

        <CodeBlock
          language="bash"
          code={`init
    Initializes AxisNet project context (Domain, Application, Infrastructure paths).

ready
    Validates AxisNet project structure and ensures environment readiness.`}
        />
      </div>

      {/* ================================================
          FEATURE GENERATION
      ================================================= */}
      <div className="bg-slate-900/60 border border-slate-800 p-7 rounded-xl mb-14 shadow-lg shadow-black/20">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-2">
          Feature Generation
        </h3>
        <p className="text-slate-400 text-sm mb-4">
          Creates a fully-scaffolded Feature module including Commands, Queries,
          Rules, Validators, Mappings, and optional Controllers and
          Repositories.
        </p>

        <CodeBlock
          language="bash"
          code={`add feature --name <FEATURE_NAME>
    Generates a complete Feature module.

    --name <FEATURE_NAME>    Name of the feature.
    --domain                 Also generates Domain entity + mappings.
    --repository             Generates repository interface + implementation.
    --controller             Generates API controller for the feature.
    --version <VERSION>      API version support (e.g., v1).
    --test                   Generates unit test boilerplates.`}
        />
      </div>

      {/* ================================================
          DOMAIN GENERATION
      ================================================= */}
      <div className="bg-slate-900/60 border border-slate-800 p-7 rounded-xl mb-14 shadow-lg shadow-black/20">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-2">
          Domain Generation
        </h3>
        <p className="text-slate-400 text-sm mb-4">
          Generates a Domain entity and optional EF Core configuration files
          using predefined templates.
        </p>

        <CodeBlock
          language="bash"
          code={`add domain --name <DOMAIN_NAME>
    Generates a new Domain entity.

add domain --name <DOMAIN_NAME> --configuration
    Also generates an EF Core configuration class.`}
        />
      </div>

      {/* ================================================
          SERVICE GENERATION
      ================================================= */}
      <div className="bg-slate-900/60 border border-slate-800 p-7 rounded-xl mb-14 shadow-lg shadow-black/20">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-2">
          Service Generation
        </h3>
        <p className="text-slate-400 text-sm mb-4">
          Creates an Application-layer service including interface +
          implementation.
        </p>

        <CodeBlock
          language="bash"
          code={`add service --name <SERVICE_NAME>
    Generates a Service template (Interface + Implementation).`}
        />
      </div>

      {/* ================================================
          CONTROLLER GENERATION
      ================================================= */}
      <div className="bg-slate-900/60 border border-slate-800 p-7 rounded-xl mb-14 shadow-lg shadow-black/20">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-2">
          Controller Generation
        </h3>
        <p className="text-slate-400 text-sm mb-4">
          Creates API controllers based on project templates, with optional
          versioning.
        </p>

        <CodeBlock
          language="bash"
          code={`add controller --name <DOMAIN_NAME>
    Generates a new API controller.

add controller --name <DOMAIN_NAME> --version <VERSION>
    Generates a versioned controller (v1, v2, etc).`}
        />
      </div>

      {/* ================================================
          REPOSITORY GENERATION
      ================================================= */}
      <div className="bg-slate-900/60 border border-slate-800 p-7 rounded-xl mb-14 shadow-lg shadow-black/20">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-2">
          Repository Generation
        </h3>
        <p className="text-slate-400 text-sm mb-4">
          Generates full Repository layer including interface and EF
          implementation.
        </p>

        <CodeBlock
          language="bash"
          code={`add repository --name <DOMAIN_NAME>
    Generates repository interface + implementation.

add repository --name <DOMAIN_NAME> --domain
    Also generates Domain entity for the repository.`}
        />
      </div>

      {/* ================================================
          TEST GENERATION
      ================================================= */}
      <div className="bg-slate-900/60 border border-slate-800 p-7 rounded-xl mb-14 shadow-lg shadow-black/20">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-2">
          Test Generation
        </h3>
        <p className="text-slate-400 text-sm mb-4">
          Generates automated unit test boilerplates including handlers, fakes,
          value generators, and mapping validations.
        </p>

        <CodeBlock
          language="bash"
          code={`add test --name <DOMAIN_NAME>
    Generates test scaffolding for Create, Update, Delete, and Query handlers.`}
        />
      </div>

      {/* ================================================
          INFORMATION COMMANDS
      ================================================= */}
      <div className="bg-slate-900/60 border border-slate-800 p-7 rounded-xl mb-14 shadow-lg shadow-black/20">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-2">
          Information & Utility Commands
        </h3>
        <p className="text-slate-400 text-sm mb-4">
          Commands used to inspect the project context or modify runtime
          configuration flags.
        </p>

        <CodeBlock
          language="bash"
          code={`info  Displays project context (ProjectName, layer paths, active flags).

help  Displays a categorized list of available CLI commands.

flags
    Shows or modifies runtime flags.

    Usage:
        axisnet flags
        axisnet flags set <FLAG_NAME> <on|off>

exit
    Ends the current AxisNet CLI session safely.`}
        />
      </div>
    </section>
  );
};

export default HelpDoc;
